<script setup lang="ts">
import { computed } from "vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";

const props = defineProps<{
  modelValue?: string[];
  options: {
    label: string;
    value: any;
  }[];
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: string[]): void;
}>();

const DND_GROUP = "sorted-list";

const visibleItems = computed({
  get: () => props.modelValue,
  set: (val: string[]) => {
    emits("update:modelValue", val);
  },
});

const toggleVisibility = (value: string) => {
  if (!visibleItems.value?.length) {
    visibleItems.value = [value];
    return;
  }
  if (visibleItems.value.includes(value)) {
    visibleItems.value = visibleItems.value.filter((item) => item !== value);
  } else {
    visibleItems.value = [...visibleItems.value, value];
  }
};

const handleDrop = (fromIndex: number, toIndex: number) => {
  if (!visibleItems.value) return;
  const reordered = [...visibleItems.value];
  const [moved] = reordered.splice(fromIndex, 1);
  const insertAt = fromIndex < toIndex ? toIndex - 1 : toIndex;
  reordered.splice(insertAt, 0, moved);
  visibleItems.value = reordered;
};

const invisibleItems = computed(() => {
  return props.options
    .map((option) => option.value)
    .filter((item) => !visibleItems.value?.includes(item));
});

const getLabel = (value: string) => {
  return props.options.find((option) => option.value === value)?.label || value;
};
</script>

<script lang="ts">
import { defineComponent, h } from "vue";
import {
  useDraggable as useDrag,
  useDroppable as useDrop,
} from "@vue-dnd-kit/core";

const SortableItem = defineComponent({
  props: {
    value: { type: String, required: true },
    index: { type: Number, required: true },
    label: { type: String, required: true },
    group: { type: String, required: true },
  },
  emits: ["toggle", "drop"],
  setup(props) {
    const { elementRef, isDragging, handleDragStart } = useDrag({
      groups: [props.group],
      data: computed(() => ({ index: props.index, value: props.value })),
    });

    return { elementRef, isDragging, handleDragStart };
  },
  render() {
    return h(
      "div",
      {
        ref: "elementRef",
        class: [
          "hoverable-card overflow-hidden p-1 ps-1 gap-2 d-flex align-items-center",
          { "sortable-dragging": this.isDragging },
        ],
      },
      [
        h(
          "div",
          {
            class: "drag-handle",
            style: { cursor: "grab", userSelect: "none" },
            onPointerdown: this.handleDragStart,
          },
          "\u22EE\u22EE"
        ),
        h("div", { class: "flex-grow-1 text-truncate" }, this.label),
        h(DRBtnIcon, {
          variant: "flat",
          size: 30,
          icon: "bi-eye",
          onClick: () => this.$emit("toggle", this.value),
        }),
      ]
    );
  },
});

const DropZone = defineComponent({
  props: {
    at: { type: Number, required: true },
    group: { type: String, required: true },
  },
  emits: ["drop"],
  setup(props, { emit }) {
    const { elementRef, isOvered } = useDrop({
      groups: [props.group],
      data: computed(() => ({ index: props.at })),
      events: {
        onDrop: async (_store, payload) => {
          const dragged = payload.items?.[0]?.data;
          if (!dragged) return false;
          emit("drop", dragged.index, props.at);
          return true;
        },
      },
    });

    return { elementRef, isOvered };
  },
  render() {
    return h("div", {
      ref: "elementRef",
      class: ["drop-zone", { "drop-zone-active": this.isOvered }],
    });
  },
});
</script>

<template>
  <div class="rounded bordered">
    <!-- Visible items with DnD -->
    <DropZone :at="0" :group="DND_GROUP" @drop="handleDrop" />
    <template v-for="(item, index) in visibleItems" :key="item">
      <SortableItem
        :value="item"
        :index="index"
        :label="getLabel(item)"
        :group="DND_GROUP"
        @toggle="toggleVisibility"
      />
      <DropZone :at="index + 1" :group="DND_GROUP" @drop="handleDrop" />
    </template>
    <!-- Invisible items -->
    <div
      v-for="item in invisibleItems"
      :key="item"
      class="p-1 py-2 hoverable-card ps-4 py disabled-row d-flex justify-content-between align-items-center"
    >
      <span>{{ getLabel(item) }}</span>
      <DRBtnIcon
        variant="flat"
        :size="30"
        @click="toggleVisibility(item)"
        icon="bi-eye-slash"
      ></DRBtnIcon>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.disabled-row {
  opacity: 0.5;
  background-color: var(--el-fill-color-light);
}

.drag-handle {
  cursor: grab;
  user-select: none;
  letter-spacing: -0.5px;
  opacity: 0.5;
  padding: 0 4px;
  font-size: 12px;
}

.sortable-dragging {
  opacity: 0.4;
}

.drop-zone {
  height: 4px;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}

.drop-zone-active {
  background-color: var(--bs-primary);
  height: 4px;
}
</style>
